import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Phone, Eye, EyeOff, Ticket, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ktcLogo from '../assets/ktc.png';
import { resolveBackendBaseUrl } from '../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const AgentLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ code: '', phone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agen/login`, formData);
      localStorage.setItem('ktc_agent_token', res.data.access_token);
      localStorage.setItem('ktc_agent', JSON.stringify(res.data.agent));
      toast.success(`Selamat datang, ${res.data.agent.name}!`);
      navigate('/agen');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login gagal';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -left-20 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[140px]" />
        <div className="absolute bottom-1/3 -right-20 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[140px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img src={ktcLogo} alt="KTC Logo" className="h-16 mx-auto mb-4" />
          </Link>
          <h1 className="font-outfit text-2xl font-bold text-white">Portal Agen</h1>
          <p className="text-gray-400 mt-2">Masuk untuk mengelola voucher & komisi Anda</p>
        </div>

        {/* Login Form */}
        <div className="bg-[#121212] rounded-2xl p-8 border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Kode Agen</label>
              <div className="relative">
                <Ticket className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="Contoh: OWN-A1B2C3"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-400 text-sm mb-2">Nomor HP</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full pl-12 pr-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white focus:border-emerald-500 focus:outline-none transition-colors"
                  placeholder="08123456789"
                  required
                />
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Gunakan kode dan nomor HP yang terdaftar di sistem admin.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-3 text-white border-0"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Ticket className="w-5 h-5 mr-2" />
                  Masuk Portal Agen
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-gray-500 text-sm">
              Belum terdaftar sebagai agen? Hubungi admin ISP untuk didaftarkan.
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke Beranda
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AgentLogin;
